import { toast } from "sonner";
import { Trophy, Copy, Calendar, Camera, Edit2, Flag, RotateCcw, Trash2 } from "lucide-react";

// iter84.70 — Multi-tournament expansion panel (iter45c), extracted from
// SiteAdmin.jsx. Lists every tournament in the rivalry with per-tournament
// actions (Schedule / Snapshots / Rename preview / End / Reopen / Delete).
export default function LeagueTournamentsPanel({
    league, tdata,
    onOpenSchedule, onOpenSnapshots, onOpenRenamePreview,
    onEnd, onReopen, onDelete,
}) {
    const lg = league;
    if (!tdata) {
        return (
            <div className="mt-4 pt-4 border-t-2 border-[#333] text-zinc-500 text-sm">Loading tournaments…</div>
        );
    }
    const tlist = tdata.tournaments || [];
    return (
        <div className="mt-4 pt-4 border-t-2 border-cyber/20 space-y-2" data-testid={`league-${lg.id}-tournaments-expansion`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    <Trophy className="w-3 h-3 text-cyber" />
                    {tlist.length} tournament{tlist.length === 1 ? "" : "s"}
                    <span className="text-zinc-700">·</span>
                    <span className="text-cyber font-bold">{tdata.tier?.toUpperCase()}</span> tier
                    <span className="text-zinc-700">·</span>
                    {tdata.active_count}/{tdata.cap} active
                </p>
            </div>
            {tlist.length === 0 && (
                <div className="brutal-card-light p-6 text-center border-dashed">
                    <p className="text-zinc-500 text-sm">No tournaments yet.</p>
                </div>
            )}
            {tlist.map(t => (
                <div
                    key={t.id}
                    data-testid={`league-${lg.id}-tournament-row-${t.id}`}
                    className={`brutal-card-light p-3 flex flex-wrap items-center justify-between gap-2 transition-colors hover:border-cyber/30 ${t.is_active ? "border-cyber/60" : ""}`}
                >
                    <div className="flex items-start gap-2.5 min-w-0 flex-1">
                        <div className={`w-9 h-9 flex items-center justify-center rounded-lg border shrink-0 mt-0.5 ${
                            t.is_active
                                ? "bg-cyber/10 border-cyber/40"
                                : t.completion_authorized_by
                                    ? "bg-zinc-800/50 border-zinc-700"
                                    : "bg-black/40 border-white/[0.08]"
                        }`}>
                            <Trophy className={`w-4 h-4 ${
                                t.is_active ? "text-cyber" : t.completion_authorized_by ? "text-zinc-500" : "text-zinc-400"
                            }`} />
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-display font-black text-sm tracking-tight">{t.name}</span>
                                {/* iter84.59.14 — Inline tournament_id copy button. */}
                                <button
                                    type="button"
                                    data-testid={`tournament-${t.id}-copy-id-btn`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigator.clipboard.writeText(t.id);
                                        toast.success("Tournament ID copied", { description: t.id });
                                    }}
                                    className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-zinc-500 hover:text-cyber transition-colors px-1.5 py-0.5 rounded border border-zinc-700/40 hover:border-cyber"
                                    title={`Copy tournament_id: ${t.id}`}
                                >
                                    <Copy className="w-2.5 h-2.5" /> ID
                                </button>
                                {t.is_active && (
                                    <span className="pill bg-cyber/20 border-cyber text-cyber text-[9px] py-0 px-1.5">DEFAULT</span>
                                )}
                                {t.completion_authorized_by && (
                                    <span className="pill bg-zinc-800 border-zinc-600 text-zinc-400 text-[9px] py-0 px-1.5">COMPLETED</span>
                                )}
                            </div>
                            <p className="text-[10px] text-zinc-500 mt-0.5">
                                {/* iter63r — Friendly label for new API-Football templates. */}
                                {({
                                    world_cup_api: "World Cup Format",
                                    world_cup_ko_api: "World Cup KO Format",
                                }[t.template] || (t.template || "").replace(/_/g, " "))} · stage: <span className="text-zinc-400">{t.current_stage || "setup"}</span> · created {t.created_at?.slice(0, 10)}
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                        <button
                            data-testid={`schedule-tournament-${t.id}-btn`}
                            onClick={() => onOpenSchedule(t.id)}
                            className="brutal-btn-ghost text-[10px] py-1.5 px-2"
                            title="Open schedule console">
                            <Calendar className="w-2.5 h-2.5" /> Schedule
                        </button>
                        <button
                            data-testid={`snapshots-tournament-${t.id}-btn`}
                            onClick={() => onOpenSnapshots({ tid: t.id, name: t.name || "—" })}
                            className="brutal-btn-ghost text-[10px] py-1.5 px-2"
                            title="View / restore draft snapshots">
                            <Camera className="w-2.5 h-2.5" /> Snapshots
                        </button>
                        {t.has_bracket_placeholders && (
                            <button
                                data-testid={`rename-preview-tournament-${t.id}-btn`}
                                onClick={() => onOpenRenamePreview({ tid: t.id, name: t.name || "—" })}
                                className="brutal-btn-ghost text-[10px] py-1.5 px-2"
                                title="Dry-run preview of the placeholder → real team rename. Editable + push on approval.">
                                <Edit2 className="w-2.5 h-2.5" /> Rename preview
                            </button>
                        )}
                        {!t.completion_authorized_by && (
                            <button
                                data-testid={`end-tournament-row-${t.id}-btn`}
                                onClick={() => onEnd(t.id)}
                                className="brutal-btn text-[10px] py-1.5 px-2 bg-blaze text-white border-black shadow-brutal">
                                <Flag className="w-2.5 h-2.5" /> End
                            </button>
                        )}
                        {t.completion_authorized_by && (
                            <button
                                data-testid={`reopen-tournament-row-${t.id}-btn`}
                                onClick={() => onReopen(t.id)}
                                className="brutal-btn text-[10px] py-1.5 px-2 bg-cyber text-black border-black shadow-brutal"
                                title="Unlock this completed tournament">
                                <RotateCcw className="w-2.5 h-2.5" /> Reopen
                            </button>
                        )}
                        <button
                            data-testid={`delete-tournament-row-${t.id}-btn`}
                            onClick={() => onDelete(t.id)}
                            className="brutal-btn-ghost text-[10px] py-1.5 px-2">
                            <Trash2 className="w-2.5 h-2.5 text-heat" /> Delete
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}
